import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadProductsToFirebase } from '../utils/uploadProducts';
import { SIZES } from '../firebase/schema';
import SampleStickerModal from '../components/SampleStickerModal';
import AddProduct from '../components/AddProduct';

const PageContainer = styled.div`
  padding: 20px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  margin-top: 80px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Button = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #50B366;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  text-transform: uppercase;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.background};
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Tr = styled.tr`
  &:hover {
    background: ${props => props.theme.colors.hover};
  }
`;

const EditButton = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 5px 10px;
  border-radius: 0;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  
  &:hover {
    background-color: #50B366;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    color: white;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    margin-bottom: 30px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62E07D;
  }

  &::placeholder {
    color: #666;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  text-transform: uppercase;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62E07D;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
`;

const ButtonGroupRight = styled.div`
  display: flex;
  gap: 12px;
`;

const SampleStickersButton = styled.button`
  background: #333;
  color: white;
  border: 1px solid #555;
  padding: 12px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #444;
  }
`;

const CancelButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #444;
  }
`;

const SaveButton = styled.button`
  background: #62E07D;
  color: black;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #50B366;
  }
`;

const Link = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  width: 50%; // Search bar takes up half the width
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.9em;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text};
    opacity: 0.6;
  }
`;

function Products() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sampleProduct, setSampleProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  // Add filtered products
  const filteredProducts = useMemo(() => {
    return products.filter(product => 
      product.styleNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm]);

  // Fetch products from Firestore
  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      const productsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const text = await file.text();
      console.log('File loaded, starting upload...');
      await uploadProductsToFirebase(text);
      console.log('Upload complete!');
      // Refresh the products list after upload
      await fetchProducts();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading products. Check console for details.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setEditForm({
      styleNo: product.styleNo,
      brand: product.brand,
      description: product.description,
      shortDescription: product.shortDescription || '',
      cogs: product.cogs,
      ssStyleId: product.ssStyleId || '',
      sanmarStyleId: product.sanmarStyleId || '',
      supplierUrl: product.supplierUrl || '',
      defaultSizeGrid: product.defaultSizeGrid || ''
    });
  };

  const handleSave = async () => {
    try {
      const productRef = doc(db, 'products', editingProduct.id);
      await updateDoc(productRef, {
        styleNo: editForm.styleNo,
        brand: editForm.brand,
        description: editForm.description,
        shortDescription: editForm.shortDescription,
        cogs: editForm.cogs,
        ssStyleId: editForm.ssStyleId || '',
        sanmarStyleId: editForm.sanmarStyleId || '',
        supplierUrl: editForm.supplierUrl,
        defaultSizeGrid: editForm.defaultSizeGrid || null,
        updatedAt: new Date()
      });
      
      // Refresh the products list
      await fetchProducts();
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product. Please try again.');
    }
  };

  const handleAddProduct = async (productData) => {
    try {
      await addDoc(collection(db, 'products'), {
        ...productData,
        cogs: parseFloat(productData.cogs) || 0,
        createdAt: new Date()
      });
      await fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error adding product. Please try again.');
      throw error;
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Products</h1>
        <HeaderActions>
          <input
            type="file"
            accept=".tab,.txt,.csv"
            onChange={handleUpload}
            style={{ display: 'none' }}
            id="fileInput"
            disabled={isUploading}
          />
          <Button onClick={() => setShowAddProduct(true)}>
            Add Product
          </Button>
          <Button 
            onClick={() => document.getElementById('fileInput').click()}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload Products'}
          </Button>
        </HeaderActions>
      </Header>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="SEARCH PRODUCTS..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>
      
      {isLoading ? (
        <div>Loading products...</div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Style No</Th>
              <Th>Brand</Th>
              <Th>Short Description</Th>
              <Th>Supplier Description</Th>
              <Th>COGS</Th>
              <Th>Default Size Grid</Th>
              <Th>S&S Style ID</Th>
              <Th>SanMar Style ID</Th>
              <Th>Supplier URL</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => (
              <Tr key={product.id}>
                <Td>{product.styleNo}</Td>
                <Td>{product.brand}</Td>
                <Td>{product.shortDescription || '-'}</Td>
                <Td>{product.description}</Td>
                <Td>${typeof product.cogs === 'number' ? product.cogs.toFixed(2) : (parseFloat(product.cogs) || 0).toFixed(2)}</Td>
                <Td>{product.defaultSizeGrid || '-'}</Td>
                <Td>{product.ssStyleId || '-'}</Td>
                <Td>{product.sanmarStyleId || '-'}</Td>
                <Td>
                  {product.supplierUrl ? (
                    <Link 
                      href={product.supplierUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Product
                    </Link>
                  ) : (
                    'No URL'
                  )}
                </Td>
                <Td>
                  <EditButton onClick={() => handleEdit(product)}>
                    Edit
                  </EditButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {editingProduct && (
        <Modal onClick={() => setEditingProduct(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>Edit Product</h2>
            <FormGroup>
              <Label>Style No *</Label>
              <Input
                value={editForm.styleNo}
                onChange={(e) => setEditForm({...editForm, styleNo: e.target.value})}
              />
            </FormGroup>
            <FormGroup>
              <Label>Brand</Label>
              <Input
                value={editForm.brand}
                onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
              />
            </FormGroup>
            <FormGroup>
              <Label>Supplier Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
              />
            </FormGroup>
            <FormGroup>
              <Label>Short Description</Label>
              <Input
                value={editForm.shortDescription}
                onChange={(e) => setEditForm({...editForm, shortDescription: e.target.value})}
                placeholder="e.g., Raglan"
              />
            </FormGroup>
            <FormGroup>
              <Label>COGS</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.cogs}
                onChange={(e) => setEditForm({...editForm, cogs: parseFloat(e.target.value)})}
              />
            </FormGroup>
            <FormGroup>
              <Label>S&S Style ID</Label>
              <Input
                value={editForm.ssStyleId || ''}
                onChange={(e) => setEditForm({...editForm, ssStyleId: e.target.value})}
                placeholder="e.g., 00760"
              />
            </FormGroup>
            <FormGroup>
              <Label>SanMar Style ID</Label>
              <Input
                value={editForm.sanmarStyleId || ''}
                onChange={(e) => setEditForm({...editForm, sanmarStyleId: e.target.value})}
                placeholder="e.g., BC3001 (STYLE# from SanMar CSV)"
              />
            </FormGroup>
            <FormGroup>
              <Label>Supplier URL</Label>
              <Input
                type="url"
                value={editForm.supplierUrl}
                onChange={(e) => setEditForm({...editForm, supplierUrl: e.target.value})}
                placeholder="https://supplier.com/product"
              />
            </FormGroup>
            <FormGroup>
              <Label>Default Size Grid</Label>
              <Select
                value={editForm.defaultSizeGrid || ''}
                onChange={(e) => setEditForm({...editForm, defaultSizeGrid: e.target.value})}
              >
                <option value="">None (user chooses per line)</option>
                {Object.keys(SIZES).map((key) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </Select>
              <small style={{ display: 'block', marginTop: 6, color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 'normal' }}>
                When this product is selected on a line, the size grid will be set to this (e.g. ADULT for 3001). Not forced—user can change it.
              </small>
            </FormGroup>
            <ButtonGroup>
              <SampleStickersButton
                type="button"
                onClick={() => setSampleProduct({
                  ...editingProduct,
                  ...editForm
                })}
              >
                Sample Stickers
              </SampleStickersButton>
              <ButtonGroupRight>
                <CancelButton onClick={() => setEditingProduct(null)}>Cancel</CancelButton>
                <SaveButton onClick={handleSave}>Save Product</SaveButton>
              </ButtonGroupRight>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {sampleProduct && (
        <SampleStickerModal
          product={sampleProduct}
          onClose={() => setSampleProduct(null)}
        />
      )}

      <AddProduct
        key={showAddProduct ? 'open' : 'closed'}
        isOpen={showAddProduct}
        onClose={() => setShowAddProduct(false)}
        onAdd={handleAddProduct}
      />
    </PageContainer>
  );
}

export default Products; 